/**
 * Award Dialog - CR-1403
 *
 * Dialog for awarding an RFQ to a winning supplier:
 * - Shows supplier summary (total quoted, lead time)
 * - Contract validity date inputs
 * - Confirmation with summary
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Award, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQ, SupplierSummary } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface AwardDialogProps {
  rfq: RFQ;
  supplier: SupplierSummary;
  onClose: () => void;
  onAward: () => void;
}

export default function AwardDialog({ rfq, supplier, onClose, onAward }: AwardDialogProps) {
  const [loading, setLoading] = useState(false);
  const [contractValidFrom, setContractValidFrom] = useState('');
  const [contractValidTo, setContractValidTo] = useState('');

  const { formatCurrency: contextFormatCurrency } = useFormatters();

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return contextFormatCurrency(num, rfq.currency, true);
  };

  const handleAward = async () => {
    if (!contractValidFrom || !contractValidTo) {
      toast.error('Please enter contract validity dates');
      return;
    }

    const fromDate = new Date(contractValidFrom);
    const toDate = new Date(contractValidTo);

    if (fromDate >= toDate) {
      toast.error('Contract end date must be after start date');
      return;
    }

    try {
      setLoading(true);
      await rfqAPI.awardRFQ(rfq.id, {
        supplier_id: supplier.id,
        contract_valid_from: contractValidFrom,
        contract_valid_to: contractValidTo,
      });
      toast.success(`RFQ awarded to ${supplier.name}`);
      onAward();
    } catch (error) {
      console.error('Error awarding RFQ:', error);
      toast.error('Failed to award RFQ');
    } finally {
      setLoading(false);
    }
  };

  // Calculate contract duration for display
  const getContractDuration = () => {
    if (!contractValidFrom || !contractValidTo) return null;
    const from = new Date(contractValidFrom);
    const to = new Date(contractValidTo);
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays >= 365) {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return months > 0 ? `${years} year(s), ${months} month(s)` : `${years} year(s)`;
    }
    if (diffDays >= 30) {
      const months = Math.floor(diffDays / 30);
      return `${months} month(s)`;
    }
    return `${diffDays} day(s)`;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-yellow-500" />
            Award RFQ
          </DialogTitle>
          <DialogDescription>
            Award this RFQ to {supplier.name}. This will create a contract and notify all suppliers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Supplier Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium">Supplier Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Supplier:</span>
                <p className="font-medium">{supplier.name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Grand Total:</span>
                <p className="font-medium text-green-600">{formatCurrency(supplier.grand_total)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Lead Time:</span>
                <p className="font-medium">{supplier.lead_time_days} days</p>
              </div>
              <div>
                <span className="text-muted-foreground">Quote Validity:</span>
                <p className="font-medium">{supplier.validity_days} days</p>
              </div>
            </div>
          </div>

          {/* Contract Validity Dates */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Contract Validity Period
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid-from">Valid From</Label>
                <Input
                  id="valid-from"
                  type="date"
                  value={contractValidFrom}
                  onChange={(e) => setContractValidFrom(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid-to">Valid To</Label>
                <Input
                  id="valid-to"
                  type="date"
                  value={contractValidTo}
                  onChange={(e) => setContractValidTo(e.target.value)}
                  min={contractValidFrom || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            {getContractDuration() && (
              <p className="text-sm text-muted-foreground">
                Contract duration: {getContractDuration()}
              </p>
            )}
          </div>

          {/* Confirmation Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800">Please confirm this action</p>
              <p className="text-yellow-700 mt-1">
                Awarding this RFQ will:
              </p>
              <ul className="list-disc list-inside text-yellow-700 mt-1 space-y-1">
                <li>Create a contract with {supplier.name}</li>
                <li>Mark other suppliers' responses as "lost"</li>
                <li>Send notifications to all participating suppliers</li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleAward} disabled={loading || !contractValidFrom || !contractValidTo}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Awarding...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Award to {supplier.name}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
